/** Daily activity, drawn as small multiples rather than one four-series chart.
 *
 * Signups, reminders and triggers differ by an order of magnitude, so plotting
 * them together would either need two y-scales (never) or flatten three of them
 * against the fourth. Four single-series panels on their own scales compare the
 * *shape* of each, which is the question this chart is actually asked.
 *
 * One series per panel means the panel title carries identity, so no legend is
 * needed and no colour has to be decoded.
 */

import { useState } from 'react'
import type { AdminTimeseriesPoint } from '../../lib/adminApi'
import { fmt } from './parts'

type Metric = keyof Omit<AdminTimeseriesPoint, 'day'>

// Validated as a categorical set against the console surface (#f2f9f8):
// lightness band, chroma floor, CVD separation and normal-vision separation all
// pass. Amber sits under 3:1 contrast, which is why every panel carries a
// visible title and value rather than relying on the colour alone.
const SERIES: { key: Metric; title: string; color: string }[] = [
  { key: 'signups', title: 'Signups', color: '#0d9488' },
  { key: 'reminders_created', title: 'Reminders created', color: '#3b82f6' },
  { key: 'reminders_completed', title: 'Reminders completed', color: '#a855f7' },
  { key: 'triggers', title: 'Nudges fired', color: '#d98324' },
]

const W = 320
const H = 76
const PAD = 3

function path(values: number[], max: number): { line: string; area: string } {
  if (values.length === 0) return { line: '', area: '' }
  const step = values.length > 1 ? (W - PAD * 2) / (values.length - 1) : 0
  const y = (v: number) => H - PAD - (max === 0 ? 0 : (v / max) * (H - PAD * 2))
  const pts = values.map((v, i) => `${PAD + i * step},${y(v)}`)
  const line = `M${pts.join('L')}`
  return { line, area: `${line}L${PAD + (values.length - 1) * step},${H}L${PAD},${H}Z` }
}

function Panel({
  title,
  color,
  points,
  metric,
  hover,
  onHover,
}: {
  title: string
  color: string
  points: AdminTimeseriesPoint[]
  metric: Metric
  hover: number | null
  onHover: (i: number | null) => void
}) {
  const values = points.map((p) => p[metric])
  const max = Math.max(1, ...values)
  const { line, area } = path(values, max)
  const total = values.reduce((a, b) => a + b, 0)
  const gid = `grad-${metric}`

  const active = hover !== null && hover < values.length ? hover : null
  const step = values.length > 1 ? (W - PAD * 2) / (values.length - 1) : 0
  const cx = active === null ? 0 : PAD + active * step
  const cy =
    active === null
      ? 0
      : H - PAD - (max === 0 ? 0 : (values[active]! / max) * (H - PAD * 2))

  return (
    <figure className="staff-trend glass">
      <figcaption>
        <span className="staff-trend__title">
          <i style={{ background: color }} aria-hidden="true" />
          {title}
        </span>
        <strong>{fmt(active === null ? total : values[active])}</strong>
        <small>
          {active === null
            ? 'in window'
            : new Date(`${points[active]!.day}T00:00:00`).toLocaleDateString(undefined, {
                day: 'numeric',
                month: 'short',
              })}
        </small>
      </figcaption>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="staff-trend__svg"
        role="img"
        aria-label={`${title}: ${total} over the window`}
        onMouseLeave={() => onHover(null)}
        onMouseMove={(e) => {
          const box = e.currentTarget.getBoundingClientRect()
          const ratio = (e.clientX - box.left) / box.width
          const i = Math.round(ratio * (values.length - 1))
          onHover(Math.max(0, Math.min(values.length - 1, i)))
        }}
      >
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.26" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${gid})`} />
        <path
          d={line}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        {active !== null && (
          <>
            <line
              x1={cx}
              y1={PAD}
              x2={cx}
              y2={H}
              stroke="var(--ink-3)"
              strokeWidth="1"
              strokeDasharray="2 3"
              vectorEffect="non-scaling-stroke"
            />
            {/* The surface-coloured ring keeps the marker legible where it
                overlaps the line it sits on. */}
            <circle cx={cx} cy={cy} r="4.5" fill={color} stroke="#f2f9f8" strokeWidth="2" />
          </>
        )}
      </svg>
    </figure>
  )
}

export function TrendGrid({ points }: { points: AdminTimeseriesPoint[] }) {
  // One hovered index for all four panels: the comparison being made is across
  // metrics on the same day, so the crosshair has to move as one.
  const [hover, setHover] = useState<number | null>(null)
  const [asTable, setAsTable] = useState(false)

  if (points.length === 0) return null

  return (
    <>
      <div className="staff-section__head">
        <h2>Daily activity</h2>
        <button className="btn btn--ghost btn--sm" onClick={() => setAsTable((v) => !v)}>
          {asTable ? 'Show charts' : 'Show table'}
        </button>
      </div>

      {asTable ? (
        <div className="staff-tablewrap glass">
          <table className="staff-table">
            <thead>
              <tr>
                <th>Day</th>
                {SERIES.map((s) => (
                  <th key={s.key} className="is-num">
                    {s.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...points].reverse().map((p) => (
                <tr key={p.day}>
                  <td>{p.day}</td>
                  {SERIES.map((s) => (
                    <td key={s.key} className="is-num">
                      {fmt(p[s.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="staff-trends">
          {SERIES.map((s) => (
            <Panel
              key={s.key}
              title={s.title}
              color={s.color}
              metric={s.key}
              points={points}
              hover={hover}
              onHover={setHover}
            />
          ))}
        </div>
      )}
    </>
  )
}
